import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { Check, Plus, Trash2, X, Loader2 } from "lucide-react";
import { useProgramYear } from "@/contexts/ProgramYearContext";

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
});

interface Goal {
  id: number;
  practice_id: number;
  category: string;
  title: string;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
  sort_order: number;
}

interface GapsToCloseProps {
  category: "quality" | "ia" | "pi" | "composite" | "cost";
  practiceId?: number;
}

export default function GapRecommendationsCard({ category, practiceId }: GapsToCloseProps) {
  const { programYear } = useProgramYear();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newTaskText, setNewTaskText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch goals from API
  const fetchGoals = useCallback(async () => {
    if (!practiceId) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/goals?category=${category}&year=${programYear}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setGoals(data);
      }
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    } finally {
      setIsLoading(false);
    }
  }, [practiceId, category, programYear]);

  // Load goals on mount
  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  // Focus input when adding mode is enabled
  useEffect(() => {
    if (isAdding && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isAdding]);

  const handleAddTask = async () => {
    if (!newTaskText.trim() || !practiceId) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/goals?year=${programYear}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          category,
          title: newTaskText.trim()
        })
      });

      if (response.ok) {
        const newGoal = await response.json();
        setGoals(prev => [...prev, newGoal]);
        setNewTaskText('');
        setIsAdding(false);
      } else {
        const error = await response.json();
        console.error('Failed to create goal:', error);
      }
    } catch (error) {
      console.error('Failed to create goal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSaving) {
      handleAddTask();
    } else if (e.key === 'Escape') {
      setIsAdding(false);
      setNewTaskText('');
    }
  };

  const toggleComplete = async (goal: Goal) => {
    if (!practiceId) return;
    
    // Optimistic update
    const newCompleted = !goal.completed;
    setGoals(prev => prev.map(g => 
      g.id === goal.id ? { ...g, completed: newCompleted } : g
    ));

    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/goals/${goal.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ completed: newCompleted })
      });

      if (!response.ok) {
        // Revert on failure
        setGoals(prev => prev.map(g => 
          g.id === goal.id ? { ...g, completed: !newCompleted } : g
        ));
      }
    } catch (error) {
      // Revert on failure
      setGoals(prev => prev.map(g => 
        g.id === goal.id ? { ...g, completed: !newCompleted } : g
      ));
      console.error('Failed to toggle goal:', error);
    }
  };

  const deleteGoal = async (goalId: number) => {
    if (!practiceId) return;
    
    // Optimistic update
    const previousGoals = goals;
    setGoals(prev => prev.filter(g => g.id !== goalId));

    try {
      const response = await fetch(`${apiUrl}/api/practices/${practiceId}/goals/${goalId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        // Revert on failure
        setGoals(previousGoals);
      }
    } catch (error) {
      // Revert on failure
      setGoals(previousGoals);
      console.error('Failed to delete goal:', error);
    }
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  return (
    <Card className="rounded-xl border-accent bg-accent/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base md:text-lg font-semibold">Actions</CardTitle>
          {!isAdding && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAdding(true)}
              className="h-7 px-2 text-xs text-muted-foreground hover:text-primary"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              Add
            </Button>
          )}
        </div>
        <Separator className="mt-2" />
      </CardHeader>
      <CardContent className="pt-0">
        {/* Three column grid for actions */}
        <div className="grid gap-3 md:grid-cols-3">
          {/* Active Goals */}
          {activeGoals.map((goal) => (
            <div 
              key={goal.id} 
              className="group flex items-start gap-3 p-3 bg-white/50 dark:bg-slate-800/50 rounded-lg min-h-[52px]"
            >
              {/* Checkbox */}
              <button
                onClick={() => toggleComplete(goal)}
                className={cn(
                  "flex-shrink-0 h-5 w-5 mt-0.5 rounded-full border-2 transition-all",
                  "border-slate-300 dark:border-slate-600",
                  "hover:border-primary hover:bg-primary/5",
                  "flex items-center justify-center"
                )}
              >
                <Check className="h-3 w-3 text-transparent" />
              </button>
              
              {/* Task text - wraps smartly */}
              <span className="flex-1 text-sm leading-snug break-words">{goal.title}</span>
              
              {/* Delete button */}
              <button
                onClick={() => deleteGoal(goal.id)}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
              </button>
            </div>
          ))}
          
          {/* Add new task input */}
          {isAdding && (
            <div className="flex items-center gap-2 p-2 rounded-lg bg-white/50 dark:bg-slate-800/50 border-2 border-dashed border-primary/30">
              <div className="flex-shrink-0 h-5 w-5 rounded-full border-2 border-slate-200 dark:border-slate-600" />
              <Input
                ref={inputRef}
                value={newTaskText}
                onChange={(e) => setNewTaskText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., Complete Measure 47 ACP..."
                className="flex-1 h-8 border-0 bg-transparent p-0 text-sm focus-visible:ring-0 placeholder:text-slate-400"
                disabled={isSaving}
              />
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleAddTask}
                  disabled={!newTaskText.trim() || isSaving}
                  className="h-7 w-7 p-0 text-primary hover:bg-primary/10"
                >
                  {isSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setIsAdding(false); setNewTaskText(''); }}
                  disabled={isSaving}
                  className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Empty state - only show when no goals at all */}
          {activeGoals.length === 0 && completedGoals.length === 0 && !isAdding && !isLoading && (
            <button
              onClick={() => setIsAdding(true)}
              className="md:col-span-3 p-4 rounded-lg border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-primary/40 hover:bg-primary/5 transition-colors group"
            >
              <div className="flex flex-col items-center gap-1 text-slate-400 group-hover:text-primary">
                <Plus className="h-5 w-5" />
                <span className="text-xs font-medium">Add an action</span>
              </div>
            </button>
          )}
          
          {/* Completed Goals */}
          {completedGoals.map((goal) => (
            <div 
              key={goal.id} 
              className="group flex items-start gap-3 p-3 rounded-lg bg-white/30 dark:bg-slate-800/30 min-h-[52px]"
            >
              {/* Completed checkbox */}
              <button
                onClick={() => toggleComplete(goal)}
                className={cn(
                  "flex-shrink-0 h-5 w-5 mt-0.5 rounded-full transition-all",
                  "bg-emerald-500 border-2 border-emerald-500",
                  "flex items-center justify-center",
                  "hover:bg-emerald-400 hover:border-emerald-400"
                )}
              >
                <Check className="h-3 w-3 text-white" />
              </button>
              
              {/* Task text (strikethrough) - wraps smartly */}
              <span className="flex-1 text-sm text-slate-400 line-through leading-snug break-words">
                {goal.title}
              </span>
              
              {/* Delete button */}
              <button
                onClick={() => deleteGoal(goal.id)}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-3.5 w-3.5 text-slate-300 hover:text-red-500" />
              </button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
